import PolygonBase from './PolygonBase';
import ShapeBase from './ShapeBase';
import shapeStar from '../assets/icons/shapeStar.svg';
import { svgToIcon } from '../utils/svg';

class ShapeStar extends PolygonBase {
  static preparedControls: ReturnType<
    typeof PolygonBase.connectControlDefinition
  >;

  calcTextMargin() {
    const { height, width } = this.fabricObject;

    const verticalBound = height / 3;
    const horizontalBound = width / 4;

    return {
      top: verticalBound,
      left: horizontalBound,
      right: horizontalBound,
      bottom: verticalBound,
    };
  }

  // A star is two pentagons, with the inner one rotated.
  static pathString() {
    const outer = this.polygonPoints(5, Math.PI * 1.5, 0.5);
    const inner = this.polygonPoints(5, Math.PI * 1.7, 0.25);
    this.dimensionsFromPoints(outer);

    const scaleX = ShapeBase.defaultWidth() / this.width;
    const scaleY = ShapeBase.defaultHeight() / this.height;

    for (const p of outer) {
      p.x *= scaleX;
      p.y *= scaleY;
    }
    for (const p of inner) {
      p.x *= scaleX;
      p.y *= scaleY;
    }

    // Compute the offset since the center of the pentagon is not in the
    // center of the bounding box using these formulae, where a is the length
    // of each side of the pentagon:
    //   rc = a / 10 * √ (50 + 10 * √5)
    //   ri = a / 10 * √ (25 + 10 * √5)
    const a = Math.hypot(outer[0].x - outer[1].x, outer[0].y - outer[1].y);
    const rc = (a / 10) * Math.sqrt(50 + 10 * Math.sqrt(5));
    const ri = (a / 10) * Math.sqrt(25 + 10 * Math.sqrt(5));
    const offsetY = (scaleY * (rc - ri)) / 2;

    let path = `M ${outer[0].x} ${outer[0].y + offsetY}\n`;
    for (let i = 1; i < 5; i++) {
      path += `L ${inner[i - 1].x} ${inner[i - 1].y + offsetY}\n`;
      path += `L ${outer[i].x} ${outer[i].y + offsetY}\n`;
    }
    path += `L ${inner[4].x} ${inner[4].y + offsetY}\n`;
    path += `Z\n`;

    // Prepare the controls while we have the geometry loaded.
    const controls = { ...super.connectControlDefinition() };
    // Position the standard controls a little differently.
    controls.cr!.x = outer[0].x;
    controls.cr!.y = outer[0].y + offsetY;
    controls.cb!.x = inner[1].x;
    controls.cb!.y = inner[1].y + offsetY;
    controls.cl!.x = outer[3].x;
    controls.cl!.y = outer[3].y + offsetY;
    controls.ct!.x = outer[4].x;
    controls.ct!.y = outer[4].y + offsetY;

    controls.aux1 = {
      x: outer[2].x,
      y: outer[2].y + offsetY,
      normal: { x: -1, y: 1 },
    };
    controls.aux2 = {
      x: outer[1].x,
      y: outer[1].y + offsetY,
      normal: { x: 1, y: 1 },
    };

    this.preparedControls = controls;

    return path;
  }

  static shapeControls = this.createDefaultControls();
  static connectControlDefinition() {
    if (!this.preparedControls) {
      this.pathString();
    }
    return this.preparedControls;
  }

  static renderIcon() {
    return svgToIcon(shapeStar);
  }
}

export default ShapeStar;
